import DOMPurify from 'isomorphic-dompurify'
import { useEffect, useRef } from 'react'

type OptionsProps = {
  ADD_TAGS?: string[]
  ADD_ATTR?: string[]
}

interface RenderRichTextContentProps {
  className?: string
  content: string
  options?: OptionsProps
  willOpenLinksInNewTab?: boolean
  skipExternalLinkHandling?: boolean
}

export const RenderRichTextContent = ({
  className,
  content,
  options = {},
  willOpenLinksInNewTab = false,
  skipExternalLinkHandling = false,
}: RenderRichTextContentProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return

    // Handle links opening in new tab
    ref.current.querySelectorAll('a').forEach((anchorTag) => {
      const href = anchorTag.getAttribute('href')
      const isExternal =
        !skipExternalLinkHandling &&
        href &&
        (href.startsWith('http://') || href.startsWith('https://')) &&
        !href.includes(window.location.hostname)

      if (willOpenLinksInNewTab || isExternal) {
        anchorTag.setAttribute('target', '_blank')
        anchorTag.setAttribute('rel', 'noopener noreferrer')
      }
    })

    // Lazy load Prism only if code tags are present
    const hasCodeTags = content.includes('<pre') || content.includes('<code')
    if (!hasCodeTags) return

    const setupPrism = async () => {
      const Prism = (await import('prismjs')).default

      // Import common languages
      await import('prismjs/components/prism-python')
      await import('prismjs/components/prism-javascript')
      await import('prismjs/components/prism-css')
      await import('prismjs/components/prism-markup')
      await import('prismjs/components/prism-json')
      await import('prismjs/components/prism-bash')
      await import('prismjs/components/prism-sql')
      await import('prismjs/components/prism-java')
      await import('prismjs/components/prism-c')
      await import('prismjs/components/prism-cpp')

      // Import plugins
      await import('prismjs/plugins/toolbar/prism-toolbar')
      await import('prismjs/plugins/copy-to-clipboard/prism-copy-to-clipboard')
      await import('prismjs/plugins/line-numbers/prism-line-numbers')

      if (ref.current) {
        ref.current.querySelectorAll('pre').forEach((pre) => {
          if (!pre.classList.contains('line-numbers')) {
            pre.classList.add('line-numbers')
          }
        })

        Prism.highlightAllUnder(ref.current)
      }
    }

    setupPrism()
  }, [content, willOpenLinksInNewTab])

  return (
    <>
      <style jsx global>{`
        /* Code Block Styling */
        .rich-text-content pre[class*='language-'] {
          position: relative;
          background-color: #0f172a !important;
          margin: 0.25rem 0 0 0 !important;
          border-radius: 0.75rem !important;
          padding: 1.5rem 1.25rem 1.25rem 1.25rem !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
          overflow-x: hidden !important;
          color: #e4e9f2 !important;
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }

        .rich-text-content code[class*='language-'] {
          font-family: 'JetBrains Mono', 'Fira Code', 'SFMono-Regular',
            ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
            'Liberation Mono', 'Courier New', monospace !important;
          font-size: 0.875rem !important;
          text-shadow: none !important;
          display: block !important;
          white-space: inherit !important;
          word-break: inherit !important;
          overflow-wrap: inherit !important;
          width: 100% !important;
        }

        .rich-text-content .code-toolbar {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 0;
          width: 100% !important;
          max-width: 100% !important;
          margin: 1rem 0;
        }

        .rich-text-content .code-toolbar > .toolbar {
          order: -1;
          position: sticky;
          display: inline-flex;
          justify-content: flex-end;
          gap: 0.4rem;
          margin-left: auto;
          background: rgba(15, 23, 42, 0.85);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 9999px;
          padding: 0.25rem 0.5rem;
          backdrop-filter: blur(6px);
          z-index: 10;
          opacity: 1 !important;
          transition: none !important;
        }

        .rich-text-content .code-toolbar > .toolbar > .toolbar-item {
          display: inline-flex;
        }

        .rich-text-content .copy-to-clipboard-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(148, 163, 184, 0.15);
          color: #e2e8f0;
          border: 1px solid rgba(148, 163, 184, 0.35);
          font-size: 0.75rem !important;
          font-weight: 600;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: all 150ms ease;
          padding: 0.2rem 0.85rem;
          border-radius: 9999px;
        }

        .rich-text-content .copy-to-clipboard-button:hover,
        .rich-text-content .copy-to-clipboard-button:focus-visible {
          color: #0f172a;
          background: #f8fafc;
          border-color: #f8fafc;
          transform: translateY(-1px);
          outline: none;
        }

        .rich-text-content
          .copy-to-clipboard-button[data-copy-state='copy-success'] {
          background: rgba(34, 197, 94, 0.2);
          border-color: rgba(34, 197, 94, 0.8);
          color: #22c55e;
        }

        .rich-text-content
          .copy-to-clipboard-button[data-copy-state='copy-error'] {
          background: rgba(248, 113, 113, 0.2);
          border-color: rgba(248, 113, 113, 0.9);
          color: #f87171;
        }

        .rich-text-content .copy-to-clipboard-button span {
          color: inherit !important;
        }

        /* Prism line numbers overrides */
        .rich-text-content pre.line-numbers {
          padding-left: 3.5rem !important;
          counter-reset: linenumber;
        }

        .rich-text-content pre.line-numbers > code {
          position: relative;
          white-space: inherit;
        }

        .rich-text-content pre.line-numbers .line-numbers-rows {
          position: absolute !important;
          pointer-events: none !important;
          top: 0rem !important;
          left: -2.5rem !important;
          font-size: 0.75rem !important;
          line-height: 1.5 !important;
          color: #94a3b8 !important;
          border-right: 1px solid rgba(148, 163, 184, 0.25);
        }

        .rich-text-content pre.line-numbers .line-numbers-rows span {
          display: block !important;
          counter-increment: linenumber;
          padding-right: 0.4rem !important;
          text-align: right !important;
        }

        .rich-text-content pre.line-numbers .line-numbers-rows span:before {
          content: counter(linenumber);
          display: block;
        }
      `}</style>
      <div
        ref={ref}
        className={`rich-text-content ${className}`}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content, options),
        }}
      />
    </>
  )
}

export default RenderRichTextContent
